import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Menu,
    MenuItem,
    CircularProgress,
    IconButton,
    Avatar,
    Typography,
    Tooltip,
    Fade
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMode } from '../context/ModeContext';
import { styled } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const LogoImage = styled('img')(({ theme }) => ({
    width: 126,
    height: 30,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    borderBottom: 'none',
    [theme.breakpoints.down('sm')]: {
        display: 'none', // Hide on mobile
    },
}));

const ModeToggle = styled(Box)(({ theme }) => ({
    display: 'flex',
    borderRadius: 32,
    overflow: 'hidden',
    backgroundColor: '#f2f2f2',
    padding: 4,
    marginRight: 16,
    position: 'relative',
    '&:hover::before': {
        opacity: 0.05,
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(52, 168, 83, 0.1)',
        opacity: 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none',
    }
}));

const ModeButton = styled(Button)(({ theme, active }) => ({
    borderRadius: 24,
    color: active ? '#FFFFFF' : '#767676',
    backgroundColor: active ? '#34A853' : 'transparent',
    '&:hover': {
        backgroundColor: active ? '#2b8e45' : '#e0e0e0',
    },
    '&:focus': {
        backgroundColor: active ? '#34A853' : 'transparent',
    },
    transition: 'all 0.2s ease-in-out',
    padding: '6px 16px',
    minWidth: 100,
    fontWeight: 500,
    fontSize: 14,
    textTransform: 'none',
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 36,
    height: 36,
    border: '2px solid #E8F5E9',
}));

const getUserInitials = (name) => {
    if (!name) return '';
    const nameParts = name.split(' ');
    return `${nameParts[0][0].toUpperCase()}${nameParts[1] ? nameParts[1][0].toUpperCase() : ''}`.trim();
};

function Header() {
    const { isLoggedIn, logout, currentUser: user } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { mode: initialMode, toggleMode } = useMode();
    const [mode, setMode] = useState(initialMode);
    const location = useLocation();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        handleMenuClose();
        navigate('/profile');
    };

    const handleBookingsClick = () => {
        handleMenuClose();
        navigate(mode === 'Borrowing' ? '/bookings' : '/lending-bookings');
    };

    const handleMyListingsClick = () => {
        handleMenuClose();
        navigate('/my-listings');
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
        navigate('/');
    };

    const handleLogin = () => {
        handleMenuClose();
        navigate('/login');
    };

    const handleRegister = () => {
        handleMenuClose();
        navigate('/register');
    };

    const handleModeSwitch = async (newMode) => {
        if (mode === newMode) return;

        setIsLoading(true);
        setMode(newMode); // Immediately update local state for visual feedback

        // Reduced delay for mode switch (300ms instead of 1000ms)
        await new Promise(resolve => setTimeout(resolve, 300));

        toggleMode(); // Update the context state
        if (newMode === 'Borrowing') {
            navigate('/listings');
        } else {
            navigate('/my-listings');
        }
        setIsLoading(false);
    };


    // if we're on the landing page, just show a centered logo
    if (location.pathname === '/') {
        return;
    }

    return (
        <StyledAppBar position="static" color="default" elevation={0}>
            <Toolbar>
                <Box
                    component={RouterLink}
                    to="/"
                    sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexGrow: 1 }}
                >
                    <LogoImage src={`${process.env.PUBLIC_URL}/logo_with_name.png`} alt="Borrowed Logo" />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isLoggedIn ? (
                        <>
                            {location.pathname !== '/profile' && (
                                <>
                                    <Tooltip
                                        title={
                                            <Typography sx={{ fontSize: 14, p: 0.5 }}>Choose your mode</Typography>
                                        }
                                        placement="bottom"
                                        TransitionComponent={Fade}
                                        TransitionProps={{ timeout: 600 }}
                                        enterDelay={300}
                                        leaveDelay={200}
                                    >
                                        <Box sx={{ position: 'relative' }}>
                                            <ModeToggle>
                                                <ModeButton
                                                    active={mode === 'Borrowing' ? 1 : 0}
                                                    onClick={() => handleModeSwitch('Borrowing')}
                                                    disabled={isLoading}
                                                >
                                                    Borrower
                                                </ModeButton>
                                                <ModeButton
                                                    active={mode === 'Lending' ? 1 : 0}
                                                    onClick={() => handleModeSwitch('Lending')}
                                                    disabled={isLoading}
                                                >
                                                    Lender
                                                </ModeButton>
                                            </ModeToggle>
                                            {isLoading && (
                                                <CircularProgress
                                                    size={24}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        marginTop: '-12px',
                                                        marginLeft: '-12px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                    <ProfileAvatar
                                        src={user?.profile_picture}
                                        onClick={handleMenuOpen}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {!user?.profile_picture && getUserInitials(user?.name)}
                                    </ProfileAvatar>
                                </>
                            )}
                            <IconButton onClick={handleMenuOpen} sx={{ ml: 0.5 }}>
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={handleProfileClick}>My Profile</MenuItem>
                                <MenuItem onClick={handleBookingsClick}>
                                    {mode === 'Borrowing' ? 'My Bookings' : 'My Booking Requests'}
                                </MenuItem>
                                {mode === 'Lending' && (
                                    <MenuItem onClick={handleMyListingsClick}>My Listings</MenuItem>
                                )}
                                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleLogin}
                                sx={{
                                    borderRadius: 4,
                                    mr: 1,
                                    textTransform: 'none',
                                    backgroundColor: '#34A853',
                                }}
                            >
                                Log in
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleRegister}
                                sx={{
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    borderColor: '#34A853',
                                    color: '#34A853'
                                }}
                            >
                                Sign up
                            </Button>
                        </>
                    )}
                </Box>
            </Toolbar>
        </StyledAppBar>
    );
}

export default Header;
